import { useEffect, useRef, useState } from "react";
import { api, AUD, PCT } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple, X, Check, Trash } from "@phosphor-icons/react";

const SOURCE_LABELS = {
    cash: "General Cash",
    contribution: "Contributions",
    dividend: "Dividends",
    interest: "Interest",
    pension: "Pension Payments",
    // Renamed generically; the "Stake Accumulate" naming is a specific broker cash
    // sweep account and shouldn't leak into the demo experience.
    accumulate_cash: "Broker Cash",
    other: "Other",
};
const SOURCE_COLORS = {
    cash: "#7B8F5C",
    contribution: "#4A6B7A",
    dividend: "#C97B5A",
    interest: "#8C6E52",
    pension: "#A88F6E",
    accumulate_cash: "#5F8071",
    other: "#B7A88F",
};

export function Pendaz() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/pendaz").then(r => setD(r.data)); }, []);
    if (!d) return <div className="text-[var(--muted)]">Loading…</div>;
    return (
        <div>
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Pendaz Business</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">The Pendaz book.</h1>
                <p className="text-[var(--muted)] mt-3">Total: <span className="text-[var(--ink)] font-medium font-mono-num">{AUD(d.total)}</span></p>
            </div>
            <div className="hairline bg-white rounded">
                <table className="editorial w-full" data-testid="pendaz-table">
                    <thead>
                    <tr><th>Asset</th><th>Type</th><th className="num">Value</th><th>Liquidity</th><th>Notes</th></tr>
                    </thead>
                    <tbody>
                    {d.assets.map(a => (
                        <tr key={a.id}>
                            <td className="font-medium">{a.asset}</td>
                            <td className="text-[var(--muted)] text-sm">{a.type}</td>
                            <td className="num font-mono-num">{AUD(a.value)}</td>
                            <td><span className="chip status-on-plan">{a.liquidity}</span></td>
                            <td className="text-[var(--muted)] text-sm">{a.notes}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function Smsf() {
    const [d, setD] = useState(null);
    const [preview, setPreview] = useState(null); // { format, entries, source_file }
    const [uploading, setUploading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const fileInputRef = useRef(null);

    const load = async () => setD((await api.get("/smsf")).data);
    useEffect(() => { load(); }, []);

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/smsf/import-preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            if (!data.entries || data.entries.length === 0) {
                toast.warning("No cash entries extracted from the file");
            } else {
                setPreview(data);
                toast.success(`Extracted ${data.entries.length} entries — please review`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to import file");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const updatePreviewEntry = (idx, patch) => {
        setPreview(p => ({
            ...p,
            entries: p.entries.map((e, i) => i === idx ? { ...e, ...patch } : e),
        }));
    };

    const removePreviewEntry = (idx) => {
        setPreview(p => ({ ...p, entries: p.entries.filter((_, i) => i !== idx) }));
    };

    const confirmImport = async () => {
        if (!preview?.entries?.length) return;
        setConfirming(true);
        try {
            const statementDate = new Date().toISOString().slice(0, 10);
            const { data } = await api.post("/smsf/import-confirm", {
                statement_date: statementDate,
                source_file: preview.source_file || "",
                entries: preview.entries.map(e => ({
                    date: e.date,
                    source: e.source,
                    amount: Number(e.amount),
                    description: e.description || "",
                })),
            });
            toast.success(`Imported ${data.inserted} entries`);
            setPreview(null);
            await load();
        } catch {
            toast.error("Import failed");
        } finally {
            setConfirming(false);
        }
    };

    const delEntry = async (id) => {
        if (!confirm("Remove this cash entry?")) return;
        try {
            await api.delete(`/smsf/cash-ledger/${id}`);
            await load();
        } catch {
            toast.error("Failed to remove");
        }
    };

    if (!d) return <div className="text-[var(--muted)]">Loading…</div>;

    const sourceKeys = Object.keys(SOURCE_LABELS);
    return (
        <div data-testid="smsf-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Self-Managed Super Fund</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">SMSF portfolios.</h1>
                </div>
                <div>
                    <input ref={fileInputRef} type="file" accept=".pdf,.csv"
                           onChange={onFileChange} style={{ display: "none" }}
                           data-testid="smsf-file-input" />
                    <button className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="smsf-import-btn">
                        <UploadSimple size={14} />
                        {uploading ? "Extracting…" : "Import statement (PDF / CSV)"}
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="hairline bg-white p-6 rounded" data-testid="smsf-total">
                    <div className="label-eyebrow">Total Balance</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(d.total_value)}</div>
                </div>
                <div className="hairline bg-white p-6 rounded" data-testid="smsf-members">
                    <div className="label-eyebrow mb-3">Member split</div>
                    <div className="space-y-1.5">
                        {(d.members || []).map(m => (
                            <div key={m.name} className="flex items-baseline justify-between gap-2 text-sm"
                                 data-testid={`smsf-member-${m.name.toLowerCase()}`}>
                                <span className="text-[var(--muted)]">
                                    {m.name} <span className="font-mono-num text-xs">({m.pct.toFixed(2)}%)</span>
                                </span>
                                <span className="font-mono-num">{AUD(m.value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="hairline bg-white p-6 rounded" data-testid="smsf-income">
                    <div className="label-eyebrow">Est. Annual Income</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(d.total_income)}</div>
                </div>
                <div className="p-6 rounded" style={{ background: "var(--eucalyptus)", color: "var(--bone)" }}
                     data-testid="smsf-yield">
                    <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>Blended Yield</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{PCT(d.yield)}</div>
                </div>
            </div>

            {/* Cash sources panel */}
            <div className="hairline bg-white p-8 rounded mb-8" data-testid="smsf-cash-sources">
                <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                    <div>
                        <div className="label-eyebrow">Cash sources</div>
                        <h2 className="font-serif text-2xl mt-1">Rolling totals</h2>
                    </div>
                    <div className="text-right">
                        <div className="label-eyebrow">Cash total</div>
                        <div className="font-serif text-3xl mt-1 font-mono-num"
                             data-testid="smsf-cash-total">{AUD(d.cash_total)}</div>
                    </div>
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                    {sourceKeys.map(k => {
                        const v = d.cash_by_source?.[k] || 0;
                        return (
                            <div key={k} className="p-3 rounded" style={{ background: "var(--line)" + "40" }}
                                 data-testid={`smsf-source-${k}`}>
                                <div className="flex items-center gap-2 label-eyebrow">
                                    <span className="inline-block w-2 h-2 rounded-full"
                                          style={{ background: SOURCE_COLORS[k] }} />
                                    {SOURCE_LABELS[k]}
                                </div>
                                <div className="font-mono-num text-lg mt-2"
                                     style={{ color: v < 0 ? "var(--terracotta)" : "var(--ink)" }}>
                                    {AUD(v)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="hairline bg-white rounded mb-8">
                <table className="editorial w-full" data-testid="smsf-table">
                    <thead>
                    <tr><th>Portfolio</th><th className="num">Value</th><th className="num">Est. Income</th><th className="num">Yield</th><th>Notes</th></tr>
                    </thead>
                    <tbody>
                    {d.portfolios.map(p => (
                        <tr key={p.id}>
                            <td className="font-medium">{p.portfolio}</td>
                            <td className="num font-mono-num">{AUD(p.value)}</td>
                            <td className="num font-mono-num">{p.est_income ? AUD(p.est_income) : "—"}</td>
                            <td className="num font-mono-num">{p.yield ? PCT(p.yield) : "—"}</td>
                            <td className="text-[var(--muted)] text-sm">{p.notes}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Cash ledger */}
            {d.cash_ledger?.length > 0 && (
                <div className="hairline bg-white rounded" data-testid="smsf-ledger">
                    <div className="p-6 border-b" style={{ borderColor: "var(--line)" }}>
                        <div className="label-eyebrow">Ledger</div>
                        <h2 className="font-serif text-2xl mt-1">Imported cash entries</h2>
                    </div>
                    <table className="editorial w-full">
                        <thead>
                            <tr><th>Date</th><th>Source</th><th>Description</th><th className="num">Amount</th><th></th></tr>
                        </thead>
                        <tbody>
                            {d.cash_ledger.slice().reverse().map(e => (
                                <tr key={e.id} data-testid={`smsf-ledger-row-${e.id}`}>
                                    <td className="text-sm">{e.date}</td>
                                    <td className="text-sm">
                                        <span className="inline-block w-2 h-2 rounded-full mr-2"
                                              style={{ background: SOURCE_COLORS[e.source] }} />
                                        {SOURCE_LABELS[e.source] || e.source}
                                    </td>
                                    <td className="text-sm text-[var(--muted)]">{e.description || "—"}</td>
                                    <td className="num font-mono-num"
                                        style={{ color: e.amount < 0 ? "var(--terracotta)" : "var(--eucalyptus)" }}>
                                        {AUD(e.amount)}
                                    </td>
                                    <td>
                                        <button onClick={() => delEntry(e.id)}
                                                className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                data-testid={`smsf-ledger-del-${e.id}`}>
                                            <Trash size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Preview modal */}
            {preview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                     style={{ background: "rgba(0,0,0,0.5)" }}
                     data-testid="smsf-preview-modal">
                    <div className="bg-white rounded max-w-5xl w-full max-h-[90vh] overflow-auto shadow-xl">
                        <div className="p-6 border-b flex items-start justify-between"
                             style={{ borderColor: "var(--line)" }}>
                            <div>
                                <div className="label-eyebrow">Import preview</div>
                                <h2 className="font-serif text-2xl mt-1">
                                    {preview.entries.length} entries from {preview.source_file}
                                </h2>
                                <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                    Edit any row before confirming. Wrong classifications? Change
                                    the Source dropdown or delete the row.
                                </p>
                            </div>
                            <button onClick={() => setPreview(null)}
                                    className="text-[var(--muted)] hover:text-[var(--ink)]"
                                    data-testid="smsf-preview-close">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 overflow-x-auto">
                            <table className="editorial w-full text-sm">
                                <thead>
                                    <tr><th>Date</th><th>Source</th><th>Description</th><th className="num">Amount</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {preview.entries.map((e, i) => (
                                        <tr key={i} data-testid={`smsf-preview-row-${i}`}>
                                            <td>
                                                <input type="date"
                                                       value={e.date || ""}
                                                       onChange={ev => updatePreviewEntry(i, { date: ev.target.value })}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm" />
                                            </td>
                                            <td>
                                                <select value={e.source}
                                                        onChange={ev => updatePreviewEntry(i, { source: ev.target.value })}
                                                        data-testid={`smsf-preview-source-${i}`}
                                                        className="border border-[var(--line)] px-2 py-1 rounded text-sm">
                                                    {sourceKeys.map(k => (
                                                        <option key={k} value={k}>{SOURCE_LABELS[k]}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input type="text" value={e.description || ""}
                                                       onChange={ev => updatePreviewEntry(i, { description: ev.target.value })}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full" />
                                            </td>
                                            <td className="num">
                                                <input type="number" step="any" value={e.amount}
                                                       onChange={ev => updatePreviewEntry(i, { amount: Number(ev.target.value) })}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-28 text-right font-mono-num" />
                                            </td>
                                            <td>
                                                <button onClick={() => removePreviewEntry(i)}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                        data-testid={`smsf-preview-del-${i}`}>
                                                    <Trash size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-2"
                             style={{ borderColor: "var(--line)" }}>
                            <button onClick={() => setPreview(null)} className="btn-ghost text-sm">Cancel</button>
                            <button onClick={confirmImport} disabled={confirming || preview.entries.length === 0}
                                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                    data-testid="smsf-preview-confirm">
                                <Check size={14} />
                                {confirming ? "Saving…" : `Confirm and save ${preview.entries.length} entries`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
